import React, { useRef, useState } from 'react';
import { Button, message, Popconfirm, Tag, Switch } from 'antd';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import type { ActionType, ProColumns } from '@ant-design/pro-table';
import { ProTable } from '@ant-design/pro-table';
import './index.less';
import EditModal from './EditModal';
import AddModal from './AddModal';
import {
  getGroupList,
  disableGroup,
  enableGroup,
  getProviderListAll,
  deleteGroup,
} from '@/services/flexGroup';

export interface GroupsType {
  id: string;
  max: number;
  current: number;
  abortStrategy: string;
  createTime: string;
  expected: number;
  name: string;
  provider: string;
  providerName: string;
  standBy: number;
  status: string;
  userId: string;
  userName: string;
  workStatusCallbackUrl: string;
  isRecycledStandbyEnable: boolean | string;
  matchToken: string
}

interface SearchType {
  name: string;
  status: string;
  provider: string;
}

// const data = new Array<GroupsType>(20).fill(2 as any).map((item, index) => ({
//     id: 'x' + index,
//     max: 6,
//     current: Number((6 - 20 / (index + 5)).toFixed(0)),
//     abortStrategy: 'aaa',
//     creatTime: '2022-10-1',
//     expected: 3,
//     name: 'ccc' + index,
//     provider: 'ali cloud',
//     providerName: 'ali cloud',
//     standBy: 1,
//     status: 'working',
//     userId: 'aaa',
//     userName: 'Ballack',
//     workStatusCallbackUrl: 'https://transfer-dev.api.patternx.gemdale.com//v1/transfer/ecs/callback'
// }))

const tags: any = {
  enable: (
    <Tag icon={<CheckCircleOutlined />} color="success">
      启用
    </Tag>
  ),
  disable: (
    <Tag icon={<MinusCircleOutlined />} color="default">
      禁用
    </Tag>
  ),
};

const Groups: React.FC = () => {
  const [editModalVisible, setEditModalVisible] = useState(false);
  const currentEditItem = useRef<GroupsType | null>(null);
  const actionRef = useRef<ActionType>();

  const [addModalVisible, setAddModalVisible] = useState(false);

  const onOption = (record: GroupsType) => {
    if (record.status === 'enable') {
      disableGroup(record.id).then((res) => {
        if (res?.data.code === 'SUCCESS') {
          message.success('停用成功');
          actionRef.current?.reload();
        }
      });
    } else {
      enableGroup(record.id).then((res) => {
        if (res?.data.code === 'SUCCESS') {
          message.success('启用成功');
          actionRef.current?.reload();
        }
      });
    }
  };
  const onDelete = (ids: string) => {
    deleteGroup(ids).then((res) => {
      if (res?.data.code === 'SUCCESS') {
        message.success('删除成功');
        actionRef.current?.reload();
        actionRef.current?.clearSelected && actionRef.current?.clearSelected();
      }
    });
  };

  const onEdit = (record: GroupsType) => {
    setEditModalVisible(true);
    currentEditItem.current = record;
  };

  const onEditModalCancel = () => {
    setEditModalVisible(false);
  };

  const onAddModalCancel = () => {
    setAddModalVisible(false);
  };

  const columns: ProColumns<GroupsType>[] = [
    {
      title: 'id',
      dataIndex: 'id',
      valueType: 'text',
      search: false,
    },
    {
      title: '名称',
      dataIndex: 'name',
      valueType: 'text',
    },
    {
      title: '供应商',
      dataIndex: 'providerName',
      formItemProps: { name: 'provider' },
      request: () => {
        return getProviderListAll().then((res) => {
          if (res) {
            const record = res.data.records;
            return Promise.resolve(
              record.map((item) => ({
                label: item.name,
                value: item.id,
              })),
            );
          }
          return Promise.resolve([]);
        });
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      valueType: 'text',
      valueEnum: {
        enable: { text: '启用' },
        disable: { text: '禁用' },
      },
      render: (_, entity) => tags[entity.status],
      // width: 80
    },
    {
      title: '最大实例数',
      dataIndex: 'max',
      search: false,
      // width: 120,
    },
    {
      title: '当前实例数',
      dataIndex: 'current',
      search: false,
      // width: 120,
    },
    {
      title: '期望实例数',
      dataIndex: 'expected',
      search: false,
      // width: 120,
    },
    {
      title: 'standBy实例数',
      dataIndex: 'standBy',
      search: false,
      // width: 120,
    },
    {
      title: 'Abort策略',
      dataIndex: 'abortStrategy',
      search: false,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      search: false,
    },

    {
      title: '创建人',
      dataIndex: 'userName',
      search: false,
    },
    {
      title: '实例工作状态接口',
      dataIndex: 'workStatusCallbackUrl',
      search: false,
      // width:220
    },
    {
      title: '动态回收standby间隔时间(小时)',
      dataIndex: 'recycledStandbyCycle',
      search: false,
    },
    {
      title: '"动态回收停机服务器间隔时间(小时)',
      dataIndex: 'recycledShutdownCycle',
      search: false,
    },
    {
      title: '是否配置动态回收standby',
      dataIndex: 'isRecycledStandbyEnable',
      search: false,
      render: (_) => [<div>{_ === '1' ? '是' : '否'}</div>],
    },
    {
      title: '动态回收standby是否在工作',
      dataIndex: 'isRecycledStandbyWork',
      search: false,
      render: (_) => [<div>{_ === '1' ? '是' : '否'}</div>],
    },
    {
      title: '匹配口令',
      dataIndex: 'matchToken',
      search: false,
    },
    {
      title: '模型地址',
      dataIndex: 'modelUrl',
      search: false,
    },
    {
      title: 'signature',
      dataIndex: 'signature',
      search: false,
    },
    {
      title: '场景',
      dataIndex: 'scenario',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      fixed: 'right',
      render: (_, entity) => [
        <Popconfirm
          title={entity.status === 'enable' ? '确定停用吗?' : '确定启用吗?'}
          onConfirm={() => onOption(entity)}
          key="stop"
        >
          <div className="optionButton">{entity.status === 'enable' ? '停用' : '启用'}</div>
        </Popconfirm>,
        <div className="optionButton" key="edit" onClick={() => onEdit(entity)}>
          编辑
        </div>,
        <Popconfirm title={'确定删除吗?'} onConfirm={() => onDelete(entity.id)} key="delete">
          <div className="optionButton">删除</div>
        </Popconfirm>,
      ],
    },
  ];

  const onAdd = () => {
    setAddModalVisible(true);
  };

  // getGroupList({}).then(res => {
  //     console.log(res)
  // })

  return (
    <div className="flexGroup">
      <ProTable<GroupsType, SearchType>
        columns={columns}
        defaultSize="large"
        scroll={{ x: 'max-content' }}
        rowSelection={false}
        rowKey={(record) => record.id}
        tableLayout={'auto'}
        actionRef={actionRef}
        toolBarRender={(_, rows) => [
          <Button key="add" type="primary" onClick={onAdd}>
            新增组
          </Button>,
          // <Popconfirm
          //     title={'确定删除吗?'}
          //     onConfirm={() => onDelete(rows.selectedRows?.map(item => item.id) || [])}
          //     key="delete"
          //     disabled={rows.selectedRows ? !rows.selectedRows.length : true}
          // >
          //     <Button type='primary' disabled={rows.selectedRows ? !rows.selectedRows.length : true} >批量删除</Button>
          // </Popconfirm>,
        ]}
        pagination={{
          pageSizeOptions: ['10', '20', '30'],
          defaultPageSize: 10,
          showSizeChanger: true,
        }}
        // onSubmit={onSearch}
        search={{ span: 6 }}
        request={async (
          params: SearchType & {
            current?: number | undefined;
            pageSize?: number | undefined;
            pageIndex?: number | undefined;
          },
          sort,
          filter,
        ) => {
          params.pageIndex = params.current;
          const data = await getGroupList(params);
          return {
            data: data?.data.records,
            success: data?.success,
            total: data?.data.total,
          };
        }}
      />
      {editModalVisible && (
        <EditModal
          visible={editModalVisible}
          record={currentEditItem.current}
          refreshTable={() => {
            actionRef.current?.reload();
          }}
          onCancel={onEditModalCancel}
        />
      )}
      {addModalVisible && (
        <AddModal
          visible={addModalVisible}
          onCancel={onAddModalCancel}
          refreshTable={() => {
            actionRef.current?.reload();
          }}
        />
      )}
    </div>
  );
};

export default Groups;
